import React, { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import tw from 'twin.macro'
import { InfoContainer } from 'src/components/shared/InfoContainer'
import { RemoteConfigKey } from 'src/constants/remoteConfig'
import { getDriver } from 'src/selectors/driver'
import { getIsPartnerDeactivated } from 'src/selectors/partner'
import { getRemoteConfigValues } from 'src/selectors/remoteConfig'
import { dismissedBannersService } from 'src/services/DismissedBannersService'
import { getHasCompletedPaymentStep } from 'src/utils/collectData'
import {
  getRemoteConfigBannerNextBoundary,
  isRemoteConfigBannerInWindow,
  parseRemoteConfigBanner,
} from 'src/utils/remoteConfigBanner'

// setTimeout fires immediately past this delay (~24.8 days)
const MAX_TIMEOUT_MS = 2 ** 31 - 1

interface SessionDismissal {
  driverId: string
  bannerId: string
}

interface Props {
  configKey: RemoteConfigKey
  className?: any
  /** For a banner inside a fixed header: the page offsets its content by this height (0 when hidden). */
  onHeightChange?(height: number): void
}

const RemoteConfigBannerComponent = ({ configKey, className, onHeightChange }: Props) => {
  const remoteConfigValues = useSelector(getRemoteConfigValues)
  const driver = useSelector(getDriver)
  const isPartnerDeactivated = useSelector(getIsPartnerDeactivated)

  const containerRef = useRef<HTMLDivElement>(null)

  const driverId = driver?.id

  const [sessionDismissal, setSessionDismissal] = useState<SessionDismissal | null>(null)
  const [boundaryTick, setBoundaryTick] = useState(0)

  const banner = useMemo(() => {
    return parseRemoteConfigBanner(remoteConfigValues[configKey])
  }, [remoteConfigValues, configKey])

  const isGettingSettlement = useMemo(() => {
    if (isPartnerDeactivated || !driver?.collectDataStepsConfigs) {
      return false
    }

    return getHasCompletedPaymentStep(driver.collectDataStepsConfigs)
  }, [isPartnerDeactivated, driver?.collectDataStepsConfigs])

  const isDismissed = useMemo(() => {
    if (!banner || !driverId) {
      return false
    }

    // Also covers blocked storage, where the stored dismissal can't be read back
    if (sessionDismissal?.driverId === driverId && sessionDismissal.bannerId === banner.id) {
      return true
    }

    return dismissedBannersService.getDismissedBannerId(configKey, driverId) === banner.id
  }, [banner, configKey, driverId, sessionDismissal])

  const handleDismiss = useCallback(() => {
    if (!banner || !driverId) {
      return
    }

    dismissedBannersService.setDismissedBannerId(configKey, driverId, banner.id)
    setSessionDismissal({ driverId, bannerId: banner.id })
  }, [banner, configKey, driverId])

  // Re-renders when the window opens or closes on a page left open across the boundary
  useEffect(() => {
    if (!banner) {
      return
    }

    const now = Date.now()
    const nextBoundary = getRemoteConfigBannerNextBoundary(banner, now)

    if (nextBoundary === null) {
      return
    }

    const timeout = setTimeout(() => {
      setBoundaryTick((tick) => {
        return tick + 1
      })
    }, Math.min(nextBoundary - now, MAX_TIMEOUT_MS))

    return () => {
      clearTimeout(timeout)
    }
  }, [banner, boundaryTick])

  const isVisible =
    !!banner &&
    !!driverId &&
    !isDismissed &&
    isRemoteConfigBannerInWindow(banner, Date.now()) &&
    (banner.audience === 'all' || isGettingSettlement)

  useEffect(() => {
    const element = containerRef.current

    if (!onHeightChange || !isVisible || !element) {
      return
    }

    const observer = new ResizeObserver(() => {
      onHeightChange(element.offsetHeight)
    })

    observer.observe(element)

    return () => {
      observer.disconnect()
      onHeightChange(0)
    }
  }, [isVisible, onHeightChange])

  if (!banner || !isVisible) {
    return null
  }

  return (
    <div ref={containerRef}>
      <InfoContainer
        className={className}
        color={banner.color}
        useInfoIcon
        onClose={banner.dismissible ? handleDismiss : undefined}
        dataTestId={`remote-config-banner-${configKey}`}
      >
        {banner.title ? (
          <Fragment>
            <span css={tw`font-semibold`}>{banner.title}</span> {banner.text}
          </Fragment>
        ) : (
          banner.text
        )}
      </InfoContainer>
    </div>
  )
}

export const RemoteConfigBanner = memo(RemoteConfigBannerComponent)
